import React, { useState } from 'react';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, LinearProgress, Collapse, IconButton,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { vortxTypography } from '@vortx/shared-ui';
import { alpha } from '@mui/material/styles';
import { COLORS, fadeInUp } from '../results/ResultsDesignTokens';

const cellSx = {
    fontFamily: vortxTypography.mono,
    fontSize: '0.7rem',
    color: COLORS.text.primary,
    borderBottom: `1px solid ${alpha(COLORS.accent, 0.08)}`,
    py: 1,
    px: 1.5,
};

const headerSx = {
    ...cellSx,
    color: COLORS.text.muted,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    fontSize: '0.6rem',
};

function getConfidenceColor(confidence) {
    if (confidence < 0.5) return COLORS.loss;
    if (confidence <= 0.7) return COLORS.solar;
    return COLORS.efficiency;
}

function summarizeDict(dict) {
    if (!dict || typeof dict !== 'object') return '--';
    return Object.entries(dict)
        .map(([k, v]) => {
            if (typeof v === 'object') return `${k}: ${JSON.stringify(v)}`;
            return `${k}: ${v}`;
        })
        .join(', ');
}

function PatternRow({ pattern }) {
    const [open, setOpen] = useState(false);
    const confColor = getConfidenceColor(pattern.confidence);

    return (
        <>
            <TableRow
                hover
                onClick={() => setOpen(!open)}
                sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: alpha(COLORS.accent, 0.04) },
                }}
            >
                <TableCell sx={cellSx} width={40}>
                    <IconButton size="small" sx={{ color: COLORS.text.secondary, p: 0 }}>
                        {open ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                    </IconButton>
                </TableCell>
                <TableCell sx={{ ...cellSx, color: COLORS.accent, fontWeight: 600 }}>
                    {pattern.pattern_id}
                </TableCell>
                <TableCell sx={cellSx}>
                    {summarizeDict(pattern.condition)}
                </TableCell>
                <TableCell sx={cellSx}>
                    {summarizeDict(pattern.recommendation)}
                </TableCell>
                <TableCell sx={cellSx} width={140}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                            variant="determinate"
                            value={pattern.confidence * 100}
                            sx={{
                                flex: 1,
                                height: 6,
                                borderRadius: 3,
                                bgcolor: alpha(confColor, 0.15),
                                '& .MuiLinearProgress-bar': {
                                    bgcolor: confColor,
                                    borderRadius: 3,
                                },
                            }}
                        />
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.65rem',
                            color: confColor,
                            fontWeight: 600,
                            minWidth: 32,
                            textAlign: 'right',
                        }}>
                            {(pattern.confidence * 100).toFixed(0)}%
                        </Typography>
                    </Box>
                </TableCell>
                <TableCell sx={{ ...cellSx, textAlign: 'center' }}>
                    {pattern.evidence_count}
                </TableCell>
                <TableCell sx={{
                    ...cellSx,
                    color: pattern.mean_pop_improvement > 0 ? COLORS.efficiency : COLORS.loss,
                    textAlign: 'right',
                }}>
                    {pattern.mean_pop_improvement != null
                        ? `${pattern.mean_pop_improvement > 0 ? '+' : ''}${pattern.mean_pop_improvement.toFixed(1)}%`
                        : '--'}
                </TableCell>
                <TableCell sx={{ ...cellSx, color: COLORS.text.muted }}>
                    {pattern.source}
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell colSpan={8} sx={{ py: 0, borderBottom: open ? `1px solid ${alpha(COLORS.accent, 0.08)}` : 'none' }}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, px: 2 }}>
                            {pattern.description && (
                                <Typography sx={{
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.7rem',
                                    color: COLORS.text.secondary,
                                    mb: 1.5,
                                }}>
                                    {pattern.description}
                                </Typography>
                            )}
                            <Box sx={{ display: 'flex', gap: 4 }}>
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={{
                                        fontFamily: vortxTypography.mono,
                                        fontSize: '0.6rem',
                                        color: COLORS.text.muted,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.1em',
                                        mb: 0.5,
                                    }}>
                                        Condition
                                    </Typography>
                                    <Box component="pre" sx={{
                                        fontFamily: vortxTypography.mono,
                                        fontSize: '0.65rem',
                                        color: COLORS.text.primary,
                                        bgcolor: alpha(COLORS.accent, 0.04),
                                        p: 1.5,
                                        borderRadius: 1,
                                        overflow: 'auto',
                                        m: 0,
                                    }}>
                                        {JSON.stringify(pattern.condition, null, 2)}
                                    </Box>
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={{
                                        fontFamily: vortxTypography.mono,
                                        fontSize: '0.6rem',
                                        color: COLORS.text.muted,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.1em',
                                        mb: 0.5,
                                    }}>
                                        Recommendation
                                    </Typography>
                                    <Box component="pre" sx={{
                                        fontFamily: vortxTypography.mono,
                                        fontSize: '0.65rem',
                                        color: COLORS.text.primary,
                                        bgcolor: alpha(COLORS.accent, 0.04),
                                        p: 1.5,
                                        borderRadius: 1,
                                        overflow: 'auto',
                                        m: 0,
                                    }}>
                                        {JSON.stringify(pattern.recommendation, null, 2)}
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

export default function PatternsTab({ patterns }) {
    if (!patterns || patterns.length === 0) {
        return (
            <Box sx={{
                p: 4,
                textAlign: 'center',
                bgcolor: COLORS.bg.card,
                border: `1px solid ${alpha(COLORS.accent, 0.1)}`,
                borderRadius: 2,
                animation: `${fadeInUp} 0.4s ease-out`,
            }}>
                <Typography sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.8rem',
                    color: COLORS.text.muted,
                }}>
                    No patterns learned yet. Run benchmarks to seed the pattern library.
                </Typography>
            </Box>
        );
    }

    return (
        <TableContainer sx={{
            bgcolor: COLORS.bg.card,
            border: `1px solid ${alpha(COLORS.accent, 0.1)}`,
            borderRadius: 2,
            animation: `${fadeInUp} 0.4s ease-out`,
        }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={headerSx} width={40} />
                        <TableCell sx={headerSx}>Pattern ID</TableCell>
                        <TableCell sx={headerSx}>Condition</TableCell>
                        <TableCell sx={headerSx}>Recommendation</TableCell>
                        <TableCell sx={headerSx} width={140}>Confidence</TableCell>
                        <TableCell sx={{ ...headerSx, textAlign: 'center' }}>Evidence</TableCell>
                        <TableCell sx={{ ...headerSx, textAlign: 'right' }}>Mean PoP Impr.</TableCell>
                        <TableCell sx={headerSx}>Source</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {patterns.map(p => (
                        <PatternRow key={p.pattern_id} pattern={p} />
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
